import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { App, Button, Checkbox, Collapse, Input, Radio, Space, Table, Typography } from 'antd';
import { STATUS, useItemSelector, useRequest, useStorePagination, useSubscribe } from '@wappsto/blanket';
import NetworkConnectionStatus from 'components/helpers/NetworkConnectionStatus';
import ShowError from 'components/alerts/ShowError';
import { useTranslation } from 'translations';
import { ITEM_ATTRIBUTES } from 'util/params';
import useShareNetworks from '../../hooks/useShareNetworks';

interface ShareTableProps {
  itemId: string;
  type: string;
  query?: string;
  onSuccess?: (...args: any[]) => any;
  onError?: (...args: any[]) => any;
  setLoading?: (...args: any[]) => any;
  addItemName?: string;
  refreshName: string;
  minSelected?: number | 'all';
  restriction?: any;
}

const ShareTable = memo((
    {
      itemId,
      type,
      query,
      onSuccess,
      onError,
      setLoading,
      addItemName,
      refreshName,
      minSelected = 1,
      restriction = {},
    }: ShareTableProps,
  ) => {
    const { tC } = useTranslation('features', 'notifications.appNotifications.permissions');
    const filter = useRef(['name']);
    const filterInput = useRef('');
    const [share, shareStatus, , error] = useShareNetworks(itemId);
    let defaultUrl = `/${type}?from_last=true&verbose=true`;
    if (query) {
      defaultUrl += `&${query}`;
    }
    const [url, setUrl] = useState(defaultUrl);
    const pageSize = 5;
    const { items, count, page, setPage, status, requests, get, itemIds } = useStorePagination({
      pageSize,
    });
    const { send: sendAcl, request: aclRequest } = useRequest();
    const loading = status === STATUS.PENDING || aclRequest?.status === STATUS.PENDING;
    const isSaving = shareStatus === STATUS.PENDING;
    const errorRequest =
      (requests.items?.status === STATUS.ERROR && requests.items) ||
      (requests.count?.status === STATUS.ERROR && requests.count);
    const [selectedRows, setSelectedRows] = useState([]);
    const [all, setAll] = useState(false);
    const [future] = useState(false);
    const addDisabled =
      (!all && selectedRows.length < (minSelected as number)) ||
      (!all && (aclRequest?.json?.id?.length || 0) + (minSelected as number) > selectedRows.length) ||
      loading ||
      isSaving;
    const addItem = useItemSelector(addItemName);
    const refreshList = useItemSelector(refreshName);
    const { message } = App.useApp();

    useEffect(() => {
      get({ url, useCache: type !== 'network' });
    }, [get, type, url]);

    // Update selected from acl
    useEffect(() => {
      if (aclRequest && aclRequest.status === STATUS.SUCCESS && aclRequest.json?.id) {
        setSelectedRows((s) => [...s, ...aclRequest.json.id]);
      }
    }, [aclRequest]);

    const getFilterObj = () => {
      const obj = {};
      filter.current.forEach((attribute) => {
        obj[attribute] = filterInput.current;
      });
      return obj;
    };

    const columns: any[] = useMemo(() => {
      const col: any[] = [
        {
          title: tC('precisePermissions.name'),
          dataIndex: ['meta', 'name_by_user'],
        },
        {
          title: tC('precisePermissions.id'),
          dataIndex: ['meta', 'id'],
        },
      ];
      if (type === 'network') {
        col.push({
          title: tC('precisePermissions.connection'),
          dataIndex: ['meta'],
          render: (meta: any) => <NetworkConnectionStatus value={meta} />,
        });
        col.push({
          title: tC('precisePermissions.created'),
          dataIndex: ['meta', 'created'],
          render: (date: any) => new Date(date).toISOString().replace('T', ' ').split('.')[0],
        });
        } else if (type === 'value') {
          col.push({
            title: tC('precisePermissions.valueType'),
            dataIndex: 'type',
          });
        }
      return col;
    }, [tC, type]);

    const onFilterChange = (e: any) => {
      filter.current = [e.target.value];
    };

    const onSearch = useCallback(
      (text) => {
        // Constructing url
        filterInput.current = text;
        let searchUrl = defaultUrl;
        if (text) {
          filter.current.forEach((f) => {
            if (f === 'id') {
              searchUrl += `&this_meta.id=${text}`;
            } else if (f === 'name') {
              searchUrl += `&this_meta.name_by_user=${text}`;
            } else {
              searchUrl += `&this_${f.replace(/ /g, '_')}=${text}`;
            }
          });
        }
        setUrl(searchUrl);
        setSelectedRows([]);
      },
      [defaultUrl],
    );

    const onSelectedRowsChange = (rows) => {
      setAll(false);
      setSelectedRows(rows);
    };

    const shareNetworks = () => {
      share(all ? url : selectedRows, future, type, getFilterObj(), restriction);
    };

    const onAllChange = (e) => {
      setAll(e.target.checked);
      setSelectedRows([]);
    };

    // const onFutureChange = useCallback((e) => {
    //   setFuture(e);
    // }, []);

    useSubscribe(type, itemIds);

    useEffect(() => {
      if (shareStatus === 'success') {
        message.open({
          type: 'success',
          content: tC('precisePermissions.successMessage'),
        });
        if (addItem && refreshList) {
          if (all || selectedRows.length > 10) {
            refreshList();
          } else {
            selectedRows.forEach((id) => {
              const matchedNetwork = items.find((network) => network.meta.id === id);
              if (matchedNetwork) {
                addItem(matchedNetwork);
              }
            });
          }
        }
        if (onSuccess) {
          let selected;
          if (all) {
            selected = getFilterObj();
          } else {
            selected = selectedRows;
          }
          onSuccess({ all, future, selected });
        }
      } else if (shareStatus === 'error' && onError) {
        onError(error);
      }
    }, [
      shareStatus,
      addItem,
      all,
      error,
      future,
      items,
      onError,
      onSuccess,
      refreshList,
      selectedRows,
      tC,
      message,
    ]);

    useEffect(() => {
      if (setLoading) {
        if (isSaving) {
          setLoading(true);
        } else {
          setLoading(false);
        }
      }
    }, [isSaving, setLoading]);

    useEffect(() => {
      sendAcl({
        method: 'GET',
        url: `/${type}?shared_with=${itemId}`,
      });
    }, [itemId, type, sendAcl]);

    return (
      <>
        {minSelected === 'all' && (
          <Space orientation="vertical" style={{ marginBottom: 20 }}>
            <Checkbox onChange={onAllChange} checked={all} disabled={loading || isSaving}>
              {tC('precisePermissions.selectAll')}
            </Checkbox>
            {/* When this value is checked, it hits an error in wappsto-redux library */}
            {/* {all && (
              <Space>
                <Typography.Text>{tC('precisePermissions.selectFuture', { type })}</Typography.Text>
                <Switch
                  onChange={onFutureChange}
                  checkedChildren={t('yes')}
                  unCheckedChildren={t('no')}
                  checked={future}
                  disabled={loading || isSaving}
                />
              </Space>
            )} */}
          </Space>
        )}
        <Space style={{ width: '100%', justifyContent: 'center', marginBottom: 12 }}>
          <Radio.Group
            options={ITEM_ATTRIBUTES.network.map((key) => ({
              label: tC(`precisePermissions.${key}`),
              value: key,
            }))}
            defaultValue="name"
            optionType="button"
            size="small"
            disabled={all}
            onChange={onFilterChange}
          />
          <Input.Search
            size="small"
            disabled={all}
            placeholder={tC('precisePermissions.searchHere')}
            style={{ width: 200 }}
            onSearch={onSearch}
          />
        </Space>
        <Table
          size="small"
          loading={loading}
          columns={columns}
          dataSource={items}
          pagination={{
            onChange: setPage,
            pageSize,
            current: page,
            total: count,
            disabled: all,
            position: ['bottomCenter'],
            showSizeChanger: false,
            hideOnSinglePage: true,
          }}
          style={{ marginBottom: 30 }}
          rowKey={(network: any) => network.meta.id}
          locale={errorRequest && { emptyText: <ShowError request={errorRequest} /> }}
          rowSelection={{
            preserveSelectedRowKeys: true,
            selectedRowKeys: selectedRows,
            onChange: onSelectedRowsChange,
            getCheckboxProps: (item: any) =>
              (aclRequest?.json?.id?.includes(item.meta.id) || isSaving || all) && {
                disabled: true,
              },
          }}
        />
        <ShowError
          request={{ status: shareStatus }}
          description={
            !error.all && (
              <>
                {error.future && <div>{tC('precisePermissions.addFutureNetworksError')}</div>}
                <Collapse ghost>
                  <Collapse.Panel key="errorPanel" header={tC('precisePermissions.errorMessage')}>
                    {error.ids.map((id: string) => (
                      <Typography.Text type="secondary" key={id}>
                        {id}
                      </Typography.Text>
                    ))}
                  </Collapse.Panel>
                </Collapse>
              </>
            )
          }
        />
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button
            type="primary"
            htmlType="submit"
            onClick={shareNetworks}
            loading={isSaving}
            disabled={addDisabled}>
            {tC('precisePermissions.addButton')}
          </Button>
        </Space>
      </>
    );
  },
);

ShareTable.displayName = 'ShareTable';

export default ShareTable;
